import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { media } from '@styles/css';

import PickLabel from '@components/chat/PickLabel';
import Badge from '@components/common/Badge';
import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';

import { useGlobalContext } from '@contexts/global';
import { IChatSituationInfo } from '@model';
import { formatNumberWithUnit } from '@utils/chat';
import { addDuration, isBefore } from '@utils/date';

const situationContainerCSS = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;

  ${media.tablet} {
    min-width: 0;
  }

  ${media.desktop} {
    min-width: 0;
  }
`;

const situationInfoCSS = (isSituation: boolean) => css`
  line-height: 14px;
  margin-top: ${Spacing.xhsmall}px;

  ${isSituation
    ? css`
        > h6 {
          ${Typography.body14}
          display: -webkit-box;
          word-wrap: break-all;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          padding-top: ${Spacing.xhsmall}px;
          margin-bottom: ${Spacing.xsmall}px;
          text-align: center;
          font-weight: 600;
          line-height: 100%;
        }
      `
    : css`
        > h6 {
          ${Typography.body12}
          line-height: 17.4px;
          display: -webkit-box;
          word-wrap: break-all;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: ${Spacing.xsmall}px;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          padding: 0 5px;
        }
      `}

  > p {
    ${Typography.body14};
    line-height: 135%;
    display: -webkit-box;
    word-wrap: break-all;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-align: center;
  }
`;

const situationImgCSS = css`
  height: 100%;
  width: 100%;

  ${media.tablet} {
    img {
      width: 100% !important;
      height: auto !important;
    }
  }
`;

const badgeContainerCSS = css`
  display: flex;
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 1;
  gap: ${Spacing.xxsmall}px;

  .new-badge {
    ${Typography.body11};
    display: flex;
    align-items: center;
  }
`;
interface SituationCardProps {
  situation: IChatSituationInfo;
  onCardClick: (id: number) => void;
  isSituation?: boolean;
}

const SituationCard = ({
  situation,
  onCardClick,
  isSituation,
}: SituationCardProps) => {
  const { t } = useTranslation();
  const { isCheming } = useGlobalContext();

  const tagTitle = formatNumberWithUnit(
    situation.stat?.created_chat_room_count
  );

  const isNewSituation =
    (situation?.released_at &&
      isBefore(
        new Date(),
        addDuration(new Date(situation?.released_at), {
          days: 3,
        })
      )) ||
    false;

  return (
    <div
      css={situationContainerCSS}
      key={situation.id}
      onClick={() => onCardClick(situation.id)}
    >
      <div css={badgeContainerCSS}>
        {isNewSituation && (
          <Badge
            text={'NEW'}
            className={'new-badge'}
            backgroundColor={
              isCheming
                ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                : 'linear-gradient(90deg, #04C8D0, #1590D5)'
            }
            radius={Radius.small}
          />
        )}
        {tagTitle && <PickLabel situation={situation} />}
      </div>
      <div css={situationImgCSS}>
        <EFinderImage
          image={situation.image}
          h={EFinderImageSizes.chatbot}
          radius={10}
          width={160}
          height={160}
          layout={'responsive'}
        />
      </div>
      <div css={situationInfoCSS(!!isSituation)}>
        <h6>{t(situation.title)}</h6>
        {isSituation && <p className="multiline">{t(situation.description)}</p>}
      </div>
    </div>
  );
};

export default SituationCard;
